// ─── Engine: Drivers ─────────────────────────────────────────────────────────
// Pure computation for all generator types. No DOM, no window globals.
// Each driver has createState() → state, tick(state, config) → { state, value }

// ─── Wind Data ──────────────────────────────────────────────────────────────
export const WIND_DATA = [
  { city: "Saint Gallen", wind_speed: 2.5, wind_direction: 45 },
  { city: "New York", wind_speed: 5.5, wind_direction: 180 },
  { city: "Tokyo", wind_speed: 3.2, wind_direction: 270 },
  { city: "London", wind_speed: 4.8, wind_direction: 220 },
  { city: "São Paulo", wind_speed: 2.1, wind_direction: 90 },
  { city: "Mumbai", wind_speed: 6.0, wind_direction: 315 },
  { city: "Sydney", wind_speed: 5.0, wind_direction: 150 },
  { city: "Lagos", wind_speed: 3.5, wind_direction: 60 },
  { city: "Berlin", wind_speed: 4.2, wind_direction: 200 },
  { city: "Mexico City", wind_speed: 1.8, wind_direction: 110 },
  { city: "Seoul", wind_speed: 3.9, wind_direction: 340 },
  { city: "Cairo", wind_speed: 4.5, wind_direction: 30 },
  { city: "Stockholm", wind_speed: 5.2, wind_direction: 260 },
  { city: "Nairobi", wind_speed: 2.8, wind_direction: 170 },
  { city: "Toronto", wind_speed: 4.0, wind_direction: 290 },
  { city: "Dubai", wind_speed: 3.0, wind_direction: 130 },
  { city: "Shanghai", wind_speed: 5.8, wind_direction: 80 },
  { city: "Cape Town", wind_speed: 7.2, wind_direction: 190 },
  { city: "Helsinki", wind_speed: 4.6, wind_direction: 310 },
  { city: "Buenos Aires", wind_speed: 3.3, wind_direction: 240 },
  { city: "Bangkok", wind_speed: 2.0, wind_direction: 100 },
  { city: "Reykjavik", wind_speed: 8.5, wind_direction: 350 },
];

// ─── Wind Helpers ───────────────────────────────────────────────────────────
export function windVectorMatrix(windSpeed, windDirection, exaggeration = 1.7, maxLength = 500) {
  const angleRad = (windDirection * Math.PI) / 180;
  const dx = Math.sin(angleRad);
  const dy = Math.cos(angleRad);
  const exaggerated = Math.min(Math.pow(windSpeed * windSpeed, exaggeration), maxLength);
  return {
    xMin: dx * exaggerated * 0.2,
    xMax: dx * exaggerated,
    yMin: dy * exaggerated * 0.2,
    yMax: dy * exaggerated,
  };
}

export function windBounce(t, minDisplacement, maxDisplacement) {
  const sine = Math.sin(t * Math.PI * 2);
  return sine >= 0 ? sine * maxDisplacement : -sine * minDisplacement;
}

// ─── Oscillator ─────────────────────────────────────────────────────────────
export function createOscillatorState() {
  return { phase: 0 };
}

export function oscillatorWave(t, waveform) {
  switch (waveform) {
    case 'square':   return t < 0.5 ? 1 : 0;
    case 'triangle': return t < 0.5 ? t * 2 : 2 - t * 2;
    case 'saw':      return t;
    case 'sine':
    default:         return (Math.sin(t * Math.PI * 2 - Math.PI / 2) + 1) / 2;
  }
}

export function tickOscillator(state, { frequency = 1, min = 0, max = 1, waveform = 'sine' }) {
  const phase = state.phase + frequency / 60;
  const wave = oscillatorWave(phase % 1, waveform);
  const value = min + wave * (max - min);
  return { state: { phase }, value };
}

// ─── Wind ───────────────────────────────────────────────────────────────────
export function createWindState() {
  return {
    index: 0,
    inChill: false,
    lastSwitch: performance.now(),
    phase: 0,
    current: { xMin: 0, xMax: 0, yMin: 0, yMax: 0 },
  };
}

export function tickWind(state, { cycleDuration = 6, chillDuration = 3, intensity = 1, exaggeration = 1.7, time = null }) {
  const now = performance.now();
  const elapsed = (now - state.lastSwitch) / 1000;
  const s = { ...state, current: { ...state.current } };

  if (s.inChill) {
    s.current.xMin += (0 - s.current.xMin) * 0.05;
    s.current.xMax += (0 - s.current.xMax) * 0.05;
    s.current.yMin += (0 - s.current.yMin) * 0.05;
    s.current.yMax += (0 - s.current.yMax) * 0.05;
    if (elapsed >= chillDuration) {
      s.inChill = false;
      s.lastSwitch = now;
      s.index = (s.index + 1) % WIND_DATA.length;
    }
  } else {
    const w = WIND_DATA[s.index];
    const t = windVectorMatrix(w.wind_speed * intensity, w.wind_direction + 90, exaggeration);
    s.current.xMin += (t.xMin - s.current.xMin) * 0.08;
    s.current.xMax += (t.xMax - s.current.xMax) * 0.08;
    s.current.yMin += (t.yMin - s.current.yMin) * 0.08;
    s.current.yMax += (t.yMax - s.current.yMax) * 0.08;
    if (elapsed >= cycleDuration) {
      s.inChill = true;
      s.lastSwitch = now;
    }
  }

  // Compute bounce from time value
  let t;
  if (time != null) {
    t = time;
  } else {
    s.phase = (state.phase || 0) + 1 / 60;
    t = s.phase % 1;
  }

  const bx = windBounce(t, s.current.xMin, s.current.xMax);
  const by = windBounce(t, s.current.yMin, s.current.yMax);

  return {
    state: s,
    bx, by,
    windSpeed: WIND_DATA[s.index].wind_speed,
    windDirection: WIND_DATA[s.index].wind_direction,
  };
}

// ─── Spring ─────────────────────────────────────────────────────────────────
export function createSpringState() {
  return { value: 0, velocity: 0 };
}

export function tickSpring(state, { target = 0, tension = 0.3, friction = 0.7 }) {
  const force = (target - state.value) * tension;
  const velocity = (state.velocity + force) * friction;
  const value = state.value + velocity;
  return { state: { value, velocity }, value };
}

// ─── Sequencer ──────────────────────────────────────────────────────────────
export function createSequencerState() {
  return { step: 0, accumulator: 0 };
}

export function tickSequencer(state, { bpm = 120, steps = [0, 0.25, 0.5, 0.75, 1] }) {
  const parsedSteps = typeof steps === 'string' ? JSON.parse(steps) : steps;
  const beatsPerFrame = (bpm / 60) / 60;
  let { step, accumulator } = state;
  accumulator += beatsPerFrame;
  if (accumulator >= 1) {
    accumulator -= 1;
    step = (step + 1) % parsedSteps.length;
  }
  return { state: { step, accumulator }, value: parsedSteps[step] };
}

// ─── Random Walk ────────────────────────────────────────────────────────────
export function createRandomWalkState() {
  return { value: 0, targetValue: 0, counter: 0, valueX: 0, valueY: 0, targetX: 0, targetY: 0 };
}

export function tickRandomWalk(state, { min = 0, max = 1, mode = 'smooth', interval = 30 }) {
  const s = { ...state };

  if (mode === 'continuous') {
    s.value = min + Math.random() * (max - min);
  } else if (mode === 'interval') {
    s.counter++;
    if (s.counter >= interval) {
      s.counter = 0;
      s.value = min + Math.random() * (max - min);
    }
  } else {
    // smooth mode
    s.counter++;
    if (s.counter >= interval) {
      s.counter = 0;
      s.targetValue = min + Math.random() * (max - min);
    }
    s.value += (s.targetValue - s.value) * 0.1;
  }

  return { state: s, value: s.value };
}

// Random walk for bounce (XY pair)
export function tickRandomWalkXY(state, { min = 0, max = 1, mode = 'smooth', interval = 30 }) {
  const s = { ...state };
  s.counter++;
  if (s.counter >= interval) {
    s.counter = 0;
    s.targetX = min + Math.random() * (max - min);
    s.targetY = min + Math.random() * (max - min);
    if (Math.random() > 0.5) s.targetX *= -1;
    if (Math.random() > 0.5) s.targetY *= -1;
  }
  if (mode === 'smooth') {
    s.valueX += (s.targetX - s.valueX) * 0.05;
    s.valueY += (s.targetY - s.valueY) * 0.05;
  } else {
    s.valueX = s.targetX;
    s.valueY = s.targetY;
  }
  return { state: s, bx: s.valueX, by: s.valueY };
}

// ─── Bounce ─────────────────────────────────────────────────────────────────
export function createBounceState() {
  return { angle: 0 };
}

export function tickBounce(state, { speed = 50, width = 6, height = 6, enabled = true, offsetX = 0, offsetY = 0 }) {
  const angle = state.angle + speed / 2000;
  if (!enabled) return { state: { angle }, bx: 0, by: 0 };
  const bx = offsetX + Math.sin(angle) * width;
  const by = offsetY + Math.cos(angle) * height;
  return { state: { angle }, bx, by };
}

// ─── Audio FFT Analysis ─────────────────────────────────────────────────────
export function analyzeFFT(freqData, { gain = 1, smoothing = 0.8, prev = null }) {
  const n = freqData.length;

  let sum = 0;
  for (let i = 0; i < n; i++) sum += freqData[i];
  const amplitude = Math.min(1, (sum / n / 255) * gain);

  let bassSum = 0;
  const bassEnd = Math.floor(n / 3);
  for (let i = 0; i < bassEnd; i++) bassSum += freqData[i];
  const bass = Math.min(1, (bassSum / bassEnd / 255) * gain);

  let trebSum = 0;
  const trebStart = Math.floor(n * 2 / 3);
  for (let i = trebStart; i < n; i++) trebSum += freqData[i];
  const treble = Math.min(1, (trebSum / (n - trebStart) / 255) * gain);

  const smooth = smoothing;
  const smoothAmplitude = prev ? prev.smoothAmplitude * smooth + amplitude * (1 - smooth) : amplitude;
  const smoothBass = prev ? prev.smoothBass * smooth + bass * (1 - smooth) : bass;
  const smoothTreble = prev ? prev.smoothTreble * smooth + treble * (1 - smooth) : treble;

  return { amplitude, bass, treble, smoothAmplitude, smoothBass, smoothTreble };
}

// ─── Typewriter ─────────────────────────────────────────────────────────────
export function createTypewriterState() {
  return { charIndex: 0, pauseCounter: 0, lastText: '' };
}

export function tickTypewriter(state, { text = '', speed = 4, loop = true, pauseAtEnd = 30 }) {
  const s = { ...state };

  if (text !== s.lastText) {
    s.lastText = text;
    s.charIndex = 0;
    s.pauseCounter = 0;
  }

  s.charIndex += speed / 60;

  if (s.charIndex >= text.length) {
    if (s.pauseCounter < pauseAtEnd) {
      s.pauseCounter++;
      s.charIndex = text.length;
    } else if (loop) {
      s.charIndex = 0;
      s.pauseCounter = 0;
    } else {
      s.charIndex = text.length;
    }
  }

  const visibleChars = Math.floor(Math.min(s.charIndex, text.length));
  return { state: s, value: text.substring(0, visibleChars) };
}
